/**
 * BloccoSquadra e RisultatoFinale — il dettaglio a lista di come una squadra
 * fa i suoi fantapunti, condiviso tra l'esito provvisorio (SimulazioneModal)
 * e il recap sotto il campo a giornata calcolata (RecapPartita).
 *
 * Per squadra: gli 11 che contano coi voti (C/VC, IN al posto di, SV, 6
 * d'ufficio, -1 fuori ruolo, D se il voto base entra nel D-Factor), sotto la
 * somma dei fantavoti, il fattore capitano, il D-Factor con la media, il
 * fairplay, il totale e i gol.
 */

import type { GiocatoreSimulato, SquadraSimulata } from "../../hooks/useFase5";
import { LogoSquadra } from "../common/LogoSquadra";
import { BadgeRuoli } from "../common/BadgeRuoli";
import { BonusMalus, haBonusMalus } from "../formazione/Pitch";
import { etichetteSonoPro, NOME_BONUS_DIFENSIVO, siglaSlot } from "../common/nomenclatura";
import { tr } from "../../i18n";

const fmtVoto = (n: number | null): string => (n === null ? "—" : n.toFixed(1));

/** "+1", "-0.5", "0": segno esplicito, al massimo due decimali. */
function fmtSegno(n: number): string {
  if (Math.abs(n) < 0.005) return "0";
  const s = Math.abs(n)
    .toFixed(2)
    .replace(/\.?0+$/, "");
  return n > 0 ? `+${s}` : `-${s}`;
}

function Eventi({ g }: { g: GiocatoreSimulato }) {
  if (!haBonusMalus(g)) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] leading-none">
      <BonusMalus v={g} />
    </span>
  );
}

function RigaGiocatore({ g, pro }: { g: GiocatoreSimulato; pro: boolean }) {
  const slot = g.slot_ruolo ?? g.ruoli_mantra[0] ?? "?";
  const bonus = g.fantavoto !== null && g.voto_base !== null ? g.fantavoto - g.voto_base : null;
  const sv = g.stato === "sv";
  return (
    // Christian 2026-11-03: altezza fissa per ogni riga (con o senza icone
    // bonus / «al posto di»), cosi' i due blocchi affiancati restano allineati.
    <div
      className={`grid grid-cols-[4.25rem_1fr_2.25rem_2.5rem_2.75rem] gap-1 items-center px-2 h-[50px] border-b border-primary-soft/40 last:border-b-0 text-[12px] ${
        sv ? "opacity-50" : ""
      }`}
    >
      {/* Andrea 2026-10-22: tutti i ruoli del giocatore in un blocco unico
          a segmenti colorati (come panchina e titolari); lo slot in cui ha
          giocato resta nel tooltip. */}
      <BadgeRuoli
        ruoli={g.ruoli_mantra}
        unito
        size="sm"
        className="self-center"
        title={siglaSlot(slot, pro)}
      />
      <div className="min-w-0">
        <div className="flex items-center gap-1 min-w-0">
          <span className="font-bold truncate">{g.nome}</span>
          {g.capitano && (
            <span className="font-mono text-[8px] bg-accent text-ink rounded px-0.5 shrink-0">
              C
            </span>
          )}
          {g.vice && (
            <span className="font-mono text-[8px] bg-info text-ink rounded px-0.5 shrink-0">
              VC
            </span>
          )}
          {g.stato === "entrato" && (
            <span className="font-mono text-[8px] bg-success text-ink rounded px-0.5 shrink-0">
              IN
            </span>
          )}
          {g.stato === "sei_ufficio" && (
            <span
              className="font-mono text-[8px] bg-warning/80 text-ink rounded px-0.5 shrink-0"
              title={tr("Deve ancora giocare: vale 6 d'ufficio")}
            >
              6*
            </span>
          )}
          {sv && (
            <span className="font-mono text-[8px] bg-fg-muted text-ink rounded px-0.5 shrink-0">
              SV
            </span>
          )}
          {g.malus_fuori_ruolo && (
            <span
              className="font-mono text-[8px] bg-danger/80 text-white rounded px-0.5 shrink-0"
              title={tr("Fuori ruolo: -1")}
            >
              -1
            </span>
          )}
          {g.in_d_factor && (
            <span
              className="font-mono text-[8px] bg-info/25 text-info border border-info/40 rounded px-0.5 shrink-0"
              title={tr("Il suo voto base entra nella media del {{v1}}", {
                v1: NOME_BONUS_DIFENSIVO,
              })}
            >
              D
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[9px] text-fg-muted leading-tight min-w-0 overflow-hidden whitespace-nowrap">
          {g.stato === "entrato" && g.sostituisce_nome && (
            <span className="truncate">al posto di {g.sostituisce_nome}</span>
          )}
          <Eventi g={g} />
        </div>
      </div>
      <span className="text-right font-mono tabular-nums">{sv ? "SV" : fmtVoto(g.voto_base)}</span>
      <span
        className={`text-right font-mono tabular-nums text-[11px] ${
          bonus === null || Math.abs(bonus) < 0.005
            ? "text-fg-muted"
            : bonus > 0
              ? "text-success"
              : "text-danger"
        }`}
      >
        {bonus === null ? "" : fmtSegno(bonus)}
      </span>
      <span className="text-right font-mono tabular-nums font-bold text-accent">
        {sv ? "0" : fmtVoto(g.fantavoto)}
      </span>
    </div>
  );
}

interface BloccoSquadraProps {
  titolo: string;
  nome: string;
  s: SquadraSimulata;
}

export function BloccoSquadra({ titolo, nome, s }: BloccoSquadraProps) {
  // Nomenclatura Pro (29/09): in Pro c'e' uno slot "Por", in Classic "P".
  const pro = etichetteSonoPro(s.giocatori.map((g) => g.slot_ruolo));
  return (
    <div className="bg-primary-deep/30 border border-primary-soft/50 rounded-card overflow-hidden">
      <div className="px-3 h-[72px] border-b border-primary-soft/50 flex items-center justify-between gap-2">
        {/* Andrea 2026-09-17: logo della squadra accanto al nome */}
        <LogoSquadra squadraId={s.squadra_id} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-wider text-fg-muted">
            {titolo}
          </div>
          <div className="font-display text-base text-accent truncate">{nome}</div>
          {s.modulo_finale && (
            <div
              className={`text-[10px] font-mono truncate ${
                s.modulo && s.modulo !== s.modulo_finale ? "text-warning" : "text-fg-muted"
              }`}
            >
              {s.modulo && s.modulo !== s.modulo_finale
                ? tr("{{v1}} → {{v2}} (cambio modulo)", { v1: s.modulo, v2: s.modulo_finale })
                : s.modulo_finale}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl tabular-nums leading-none">
            {s.punteggio_totale.toFixed(1)}
          </div>
          <div className="text-[9px] text-fg-muted font-mono uppercase">{tr("fantapunti")}</div>
        </div>
      </div>

      {!s.formazione_consegnata ? (
        <div className="px-3 py-4 text-sm text-danger">{s.nota}</div>
      ) : (
        <>
          <div className="grid grid-cols-[4.25rem_1fr_2.25rem_2.5rem_2.75rem] gap-1 px-2 py-1 text-[9px] uppercase tracking-wider font-mono text-fg-muted border-b border-primary-soft/50">
            <span>Rl</span>
            <span>{tr("Giocatore")}</span>
            <span className="text-right">{tr("Voto")}</span>
            <span className="text-right">+/−</span>
            <span className="text-right">FV</span>
          </div>
          {s.giocatori.map((g) => (
            <RigaGiocatore key={`${g.giocatore_id}-${g.slot_ruolo}`} g={g} pro={pro} />
          ))}
        </>
      )}

      {/* Fattori sotto i voti, poi il totale */}
      <div className="px-3 py-2 border-t border-primary-soft/50 space-y-1 text-[12px]">
        <div className="flex justify-between">
          <span className="text-fg-muted">{tr("Somma fantavoti")}</span>
          <span className="font-mono tabular-nums">{s.punteggio_voto.toFixed(1)}</span>
        </div>
        {s.formazione_consegnata && (
          <div className="flex justify-between">
            <span className="text-fg-muted">
              Fattore capitano{s.capitano_bonus_nome ? ` (${s.capitano_bonus_nome})` : ""}
            </span>
            <span className="font-mono tabular-nums">{fmtSegno(s.bonus_capitano)}</span>
          </div>
        )}
        {s.formazione_consegnata && s.modificatore_difesa_attivo && (
          <div className="flex justify-between">
            <span className="text-fg-muted">
              {NOME_BONUS_DIFENSIVO}
              {s.media_difesa !== null && s.media_difesa !== undefined
                ? tr(" (media {{v1}})", { v1: s.media_difesa.toFixed(2) })
                : tr(" (non si applica)")}
            </span>
            <span className="font-mono tabular-nums">{fmtSegno(s.bonus_difesa)}</span>
          </div>
        )}
        {s.formazione_consegnata && s.fairplay_attivo && (
          <div className="flex justify-between">
            <span className="text-fg-muted">{tr("Fairplay")}</span>
            <span className="font-mono tabular-nums">{fmtSegno(s.bonus_fairplay)}</span>
          </div>
        )}
        <div className="flex justify-between pt-1 border-t border-primary-soft/40 font-bold">
          <span>{tr("Totale")}</span>
          <span className="font-mono tabular-nums text-accent">
            {s.punteggio_totale.toFixed(1)}
          </span>
        </div>
        <div className="flex justify-between font-bold">
          <span>{tr("Gol")}</span>
          <span className="font-mono tabular-nums">{s.gol}</span>
        </div>
      </div>
    </div>
  );
}

/** I modificatori di squadra come pastiglie (Andrea 2026-09-17): fattore
 * capitano, D-Factor, fairplay e i -1 dei fuori ruolo. Si vedono solo quelli
 * in gioco: una regola spenta in lega non compare, una accesa che non ha
 * dato niente compare a 0. I fuori ruolo sono gia' dentro i fantavoti: qui
 * si ricordano soltanto. */
export function ModificatoriSquadra({
  s,
  allinea = "center",
}: {
  s: SquadraSimulata;
  allinea?: "center" | "start" | "end";
}) {
  if (!s.formazione_consegnata) return null;
  const fuoriRuolo = s.giocatori.filter((g) => g.malus_fuori_ruolo && g.stato !== "sv").length;
  const voci: Array<{ key: string; label: string; valore: number; title: string }> = [
    {
      key: "cap",
      label: tr("Capitano"),
      valore: s.bonus_capitano,
      title: s.capitano_bonus_nome
        ? tr("Fattore capitano: {{v1}}", { v1: s.capitano_bonus_nome })
        : tr("Fattore capitano"),
    },
  ];
  if (s.modificatore_difesa_attivo) {
    voci.push({
      key: "dif",
      label: NOME_BONUS_DIFENSIVO,
      valore: s.bonus_difesa,
      title:
        s.media_difesa !== null && s.media_difesa !== undefined
          ? tr("{{v1}}: media difesa {{v2}}", {
              v1: NOME_BONUS_DIFENSIVO,
              v2: s.media_difesa.toFixed(2),
            })
          : tr("{{v1}}: non si applica", { v1: NOME_BONUS_DIFENSIVO }),
    });
  }
  if (s.fairplay_attivo) {
    voci.push({
      key: "fair",
      label: tr("Fairplay"),
      valore: s.bonus_fairplay,
      title: tr("Fairplay"),
    });
  }
  if (fuoriRuolo > 0) {
    voci.push({
      key: "fuori",
      label: tr("Fuori ruolo"),
      valore: -fuoriRuolo,
      title: tr("{{v1}} fuori ruolo: -1 a testa, gia' compreso nei fantavoti", { v1: fuoriRuolo }),
    });
  }
  const giustifica =
    allinea === "start" ? "justify-start" : allinea === "end" ? "justify-end" : "justify-center";
  return (
    <div className={`flex flex-wrap gap-1 ${giustifica}`}>
      {voci.map((x) => (
        <span
          key={x.key}
          title={x.title}
          className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-mono leading-none border ${
            Math.abs(x.valore) < 0.005
              ? "border-primary-soft/50 text-fg-muted"
              : x.valore > 0
                ? "border-success/40 bg-success/10 text-success"
                : "border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          <span className="uppercase tracking-wide">{x.label}</span>
          <span className="font-bold tabular-nums">{fmtSegno(x.valore)}</span>
        </span>
      ))}
    </div>
  );
}

interface RisultatoFinaleProps {
  etichetta: string;
  nomeCasa: string;
  nomeTrasferta: string;
  golCasa: number;
  golTrasferta: number;
  fpCasa: number | null;
  fpTrasferta: number | null;
  /** Andrea 2026-09-17: coi loghi accanto ai nomi, se si sa chi gioca. */
  squadraCasaId?: string;
  squadraTrasfertaId?: string;
}

/** Il riquadro col risultato in fondo al dettaglio. */
export function RisultatoFinale({
  etichetta,
  nomeCasa,
  nomeTrasferta,
  golCasa,
  golTrasferta,
  fpCasa,
  fpTrasferta,
  squadraCasaId,
  squadraTrasfertaId,
}: RisultatoFinaleProps) {
  return (
    <div className="rounded-card border border-accent/50 bg-accent/10 px-4 py-4 text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-accent mb-2">
        {etichetta}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="flex items-center justify-end gap-2 min-w-0">
          <span className="font-display text-sm sm:text-base leading-tight break-words text-right min-w-0">
            {nomeCasa}
          </span>
          {squadraCasaId && <LogoSquadra squadraId={squadraCasaId} size={32} />}
        </div>
        <div className="font-display text-4xl tabular-nums">
          {golCasa} - {golTrasferta}
        </div>
        <div className="flex items-center justify-start gap-2 min-w-0">
          {squadraTrasfertaId && <LogoSquadra squadraId={squadraTrasfertaId} size={32} />}
          <span className="font-display text-sm sm:text-base leading-tight break-words text-left min-w-0">
            {nomeTrasferta}
          </span>
        </div>
      </div>
      {fpCasa !== null && fpTrasferta !== null && (
        <div className="text-[11px] text-fg-muted font-mono mt-1">
          {fpCasa.toFixed(1)} · {fpTrasferta.toFixed(1)} FP
        </div>
      )}
    </div>
  );
}
